import { inject, Injectable } from '@angular/core';
import { AlertType } from '@gains-com/ui-angular';
import { ApiService, ErrorMapType } from './api.service';
import { RedemptionAndPayment } from './payments-common';
import { lastValueFrom, take } from 'rxjs';
import { UserViewService } from './view-states/user-view.service.js';
import { CurrencyId } from '@gains-com/shared';
import { environment } from '../../environments/environment.js';

export interface FeeEstimate {
  transactionCurrency: CurrencyId;
  feeAmountInTransactionCurrency: string;
  feeCurrency: CurrencyId;
  feeConversionRate: string;
}

export type CoinflowWithdrawalSpeed = 'asap' | 'same_day' | 'standard' | 'card' | 'venmo';

export interface RedemptionRequirements {
  document_verification_required: boolean;
}

export interface CoinflowFeeEstimate extends FeeEstimate {
  speed: CoinflowWithdrawalSpeed;
}

@Injectable({
  providedIn: 'root',
})
export class RedemptionsService extends ApiService {
  private readonly _userView = inject(UserViewService);

  readonly ErrorMap: ErrorMapType = {
    INTERNAL_SERVER_ERROR: {
      swallowError: false,
      title: 'Unknown Error',
      message:
        'An unknown problem occurred. Please contact customer support if the problem persists.',
      type: AlertType.Danger,
    },
    INVALID_PAYMENT_METHOD: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'Redemption payment method is invalid. Try using a different payment method.',
    },
    AMOUNT_UNDER_MINIMUM: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'Redemption amount is less than the minimum required. Please try an amount greater than $50.',
    },
    INSUFFICIENT_FUNDS: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message: 'Insufficient SC to complete redemption.',
    },
    UNSUPPORTED_ASSET: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'Asset is not supported for this operation. Try redeeming another asset.',
    },
    DUPLICATE_TRANSACTION: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption Failed',
      message: 'Redemption has already been submitted.',
    },
    KYC_NOT_VERIFIED: {
      type: AlertType.Warning,
      swallowError: false,
      title: 'Redemption Failed',
      message:
        'KYC verification is still processing. Please wait a minute and try again.',
    },
    PAYMENT_PROVIDER_BLOCKED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Redemption unavailable',
      message:
        'Our payment processor is unable to offer debit card, bank account or Venmo withdrawals for your account. Please choose crypto or contact support.',
    },
    ...Object.fromEntries(
      [
        'INVALID_TRANSACTION',
        'COULD_NOT_RECONCILE_PAYMENT_USER',
        'COULD_NOT_RECONCILE_TRANSACTION',
        'TRANSACTION_FAILED',
        'AUTOFUEL_FAILED',
      ].map((error) => [
        error,
        {
          type: AlertType.Danger,
          swallowError: false,
          title: 'Redemption Failed',
          message: 'An unexpected error occurred. Please try again.',
        },
      ])
    ),
  };

  async estimateCoinflowRedemptionFee(amount: string) {
    return await this.post<CoinflowFeeEstimate[]>('/fee-estimates/coinflow', {
      amount,
    });
  }

  async estimateFireblocksRedemptionFee(
    currencyId: CurrencyId,
    amount: string,
    feeLevel: 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM'
  ) {
    return await this.post<FeeEstimate>('/fee-estimates/fireblocks', {
      currencyId,
      amount,
      feeLevel,
    });
  }

  async createRedemption(
    idempotencyKey: string,
    paymentMethodId: string,
    amount: string
  ) {
    const redemption = await lastValueFrom<RedemptionAndPayment>(
      this._http.post<RedemptionAndPayment>(`${environment.apiUrl}/redemptions`, {
        idempotencyKey,
        paymentMethodId,
        amount,
      }).pipe(take(1))
    );

    this._userView.updateUser({
      gc_balance: redemption.gc_balance,
      sc_balance: redemption.sc_balance,
    });

    return redemption;
  }

  getRedemptions$(q?: {
    start?: Date;
    end?: Date;
    limit?: number;
    status?: string | string[];
    sortDirection?: 'asc' | 'desc';
  }) {
    const { start, end, limit, status, sortDirection } = q ?? {};

    const qs = new URLSearchParams({
      ...(start ? { start: start.toISOString() } : {}),
      ...(end ? { end: end.toISOString() } : {}),
      ...(limit ? { limit: limit.toString() } : {}),
      ...(sortDirection ? { sortDirection } : {}),
    });

    if (status) {
      Array.isArray(status)
        ? status.forEach((status) => qs.append('status', status))
        : qs.append('status', status);
    }

    return this.get$<{ total_count: number; results: RedemptionAndPayment[] }>(
      `/redemptions?${qs.toString()}`
    );
  }

  async getRedemptions(q?: {
    start?: Date;
    end?: Date;
    limit?: number;
    status?: string | string[];
    sortDirection?: 'asc' | 'desc';
  }) {
    return lastValueFrom(this.getRedemptions$(q));
  }

  /**
   * Server-authoritative answer to "must this user upload a document before redeeming?",
   * computed from the same inputs as the transaction check that blocks the redemption.
   *
   * Deliberately bypasses `get$` so a failure surfaces as a rejection instead of an
   * error alert plus `null` — the caller falls back to the legacy client-side derivation.
   */
  async getRedemptionRequirements() {
    return lastValueFrom(
      this._http
        .get<RedemptionRequirements>(`${environment.apiUrl}/redemptions/requirements`)
        .pipe(take(1))
    );
  }

  async cancelRedemption(redemptionId: string) {
    return await this.delete(`/redemptions/${redemptionId}`);
  }
}
